// Want-list board server functions.
//   • listWantBoard is public: it feeds the crawlable /want-list page during SSR
//     and returns no account identifiers.
//   • Everything that changes a listing is authenticated and writes through the
//     caller's own client, so RLS plus the trade_wants trigger decide ownership
//     (the client can never post a listing under someone else's account).
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { wantEntrySchema, wantIdSchema, type WantEntry } from "./want-list-shared";

/** Every active want/have listing for the public board. */
export const listWantBoard = createServerFn({ method: "GET" }).handler(async (): Promise<WantEntry[]> => {
  try {
    const { readWantBoard } = await import("./want-list.server");
    return await readWantBoard();
  } catch (e) {
    console.error("listWantBoard failed", e);
    return [];
  }
});

/** The signed-in member's own listings, including any they deactivated. */
export const getMyWantList = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<WantEntry[]> => {
    const { data, error } = await context.supabase
      .from("trade_wants")
      .select("id, kind, card_code, note, owner_name, created_at")
      .eq("user_id", context.userId)
      .eq("active", true)
      .order("created_at", { ascending: false });
    if (error) throw error;
    const { isOfficialCardCode } = await import("./tcg-cards");
    return ((data ?? []) as unknown as WantEntry[]).filter((r) => isOfficialCardCode(r.card_code));
  });

/** Post a card the member wants, or a spare they can trade away. */
export const addWantEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => wantEntrySchema.parse(input))
  .handler(async ({ data, context }) => {
    const { resolveOfficialCardCode } = await import("./tcg-cards");
    const code = resolveOfficialCardCode(data.cardCode);
    if (!code) {
      throw new Error(
        `"${data.cardCode}" is not an official Palworld TCG printing. Pick a card from the suggestions (for example EBP01-001).`,
      );
    }
    const { error } = await context.supabase.from("trade_wants").upsert(
      {
        user_id: context.userId,
        kind: data.kind,
        card_code: code,
        note: data.note,
        active: true,
      },
      { onConflict: "user_id,kind,card_code" },
    );
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

/** Take one of the member's own listings off the board. */
export const removeWantEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => wantIdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("trade_wants")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
